import { useAiRecommendationCta, useContextualJourneyIds } from '@cobliteam/ai';
import type { FleetAiRecommendationV1 } from '@cobliteam/api-lib';
import { useAiRecommendationsStore } from '@cobliteam/hooks';
import { type AiRecommendationJourney, permissionsConfig } from '@cobliteam/hooks/src/useAiRecommendationsStore/useAiRecommendationsStore';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { useSuggestion } from '@cobliteam/hooks/src/useSuggestion';
import { useTranslation } from '@cobliteam/i18n';
import usePermissions from '@cobliteam/permissions/src/hooks/usePermissions.tsx';
import Button from '@cobliteam/ui/components/Button';
import Carousel, { type CarouselApi, CarouselContent, CarouselItem } from '@cobliteam/ui/components/Carousel';
import Tag from '@cobliteam/ui/components/Tag';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { useEffect, useState } from 'react';
import CobliConectaSuggestionSlide from '../CobliConectaSuggestion/CobliConectaSuggestionSlide';
import { useShouldShowCobliConecta } from '../CobliConectaSuggestion/useShouldShowCobliConecta';
import AiImage from './assets/ai-image.svg?react';

const criticalityOrder: Record<string, number> = { critical: 0, urgent: 1, important: 2, suggestive: 3 };
const MAX_VISIBLE_AI = 3;
const COBLI_CONECTA_SUGGESTION_ID = 'cobli-conecta-download';

type Slide = { kind: 'suggestion'; key: string } | { kind: 'ai'; key: string; recommendation: FleetAiRecommendationV1 };

const RecommendationCard = ({
  recommendation,
  isContextual,
  dismissLabel,
}: {
  recommendation: FleetAiRecommendationV1;
  isContextual: boolean;
  onDismiss: () => void;
  dismissLabel: string;
}) => {
  const { onCta, ctaLabel } = useAiRecommendationCta({
    component: 'ai-recommendation-floating-card',
    journeyId: recommendation.journeyId as AiRecommendationJourney,
    aiPrompt: recommendation.aiPrompt,
    disableAiChat: true,
    forceFloatingChat: isContextual,
  });

  const { dismissRecommendation } = useAiRecommendationsStore();

  const handleDismissRecommendation = () => {
    dismissRecommendation(recommendation.journeyId, recommendation.recommendationType);
  };

  return (
    <div className='flex w-full flex-col gap-[9px]'>
      <Typography variant='description-title' className='text-[#0a0a0a]'>
        {recommendation.journeyName}
      </Typography>

      <Typography variant='description-list-default' color='secondary' className='text-start'>
        {recommendation.shortDescription}
      </Typography>

      <div className='flex items-center justify-between gap-4 pt-1'>
        <Button variant='ghost' color='primary' size='sm' onClick={handleDismissRecommendation}>
          {dismissLabel}
        </Button>
        <Button variant='contained' color='primary' size='sm' onClick={onCta}>
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
};

const AiRecommendationFloatingCard = () => {
  const { t } = useTranslation('app-header');
  const { t: tSuggestions } = useTranslation('suggestions');
  const { aiRecommendations2 } = useFeatureFlags();
  const { isAccessAllowed } = usePermissions();

  const { recommendations, floatingCardDismissed, dismissFloatingCard, isError } = useAiRecommendationsStore();

  const { shouldShow: shouldShowCobliConecta, dismiss: dismissCobliConecta, accept: acceptCobliConecta } = useSuggestion(COBLI_CONECTA_SUGGESTION_ID, useShouldShowCobliConecta);

  const contextualJourneys = useContextualJourneyIds();
  const isContextual = contextualJourneys.length > 0;

  const allowedRecommendations = recommendations.filter((item) => isAccessAllowed(permissionsConfig[item.journeyId]));

  const globalSorted = allowedRecommendations.sort((a, b) => (criticalityOrder[a.criticality] ?? 99) - (criticalityOrder[b.criticality] ?? 99)).slice(0, MAX_VISIBLE_AI);

  const aiSorted = isContextual ? globalSorted.filter((recommendation) => contextualJourneys.includes(recommendation.journeyId as AiRecommendationJourney)) : globalSorted;

  const aiSlidesVisible = aiRecommendations2 && !isError;
  const slides: Slide[] = [
    ...(shouldShowCobliConecta ? [{ kind: 'suggestion' as const, key: COBLI_CONECTA_SUGGESTION_ID }] : []),
    ...(aiSlidesVisible ? aiSorted.map((recommendation) => ({ kind: 'ai' as const, key: recommendation.journeyId, recommendation })) : []),
  ];

  const [api, setApi] = useState<CarouselApi>();
  const [contentHeight, setContentHeight] = useState<number | undefined>(undefined);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!api) return;
    const updateActiveHeight = () => {
      const slide = api.slideNodes()[api.selectedScrollSnap()];
      if (slide) {
        const child = slide.firstElementChild as HTMLElement | null;
        setContentHeight(child?.offsetHeight ?? slide.offsetHeight);
      }
      setSelectedIndex(api.selectedScrollSnap());
    };
    updateActiveHeight();
    api.on('select', updateActiveHeight);
    api.on('reInit', updateActiveHeight);
    return () => {
      api.off('select', updateActiveHeight);
      api.off('reInit', updateActiveHeight);
    };
  }, [api]);

  if (floatingCardDismissed || slides.length === 0) return null;

  const activeSlide = slides[Math.min(selectedIndex, slides.length - 1)];
  const isSuggestionActive = activeSlide?.kind === 'suggestion';

  return (
    <div className='fixed bottom-4 left-[calc(var(--sidebar-width-icon)+1rem)] z-[1003] w-fit max-w-[360px]'>
      <div className='flex w-full flex-col gap-[9px] rounded-2xl border border-[#d9d9e0] border-solid bg-[#fcfdfe] p-[14px] shadow-[0_4px_6px_-2px_rgba(0,0,0,0.05),0_10px_15px_-3px_rgba(0,0,0,0.1)]'>
        {/* Header: tag + (AI icon for AI slides) + close */}
        <div className='flex w-full items-center justify-between'>
          {isSuggestionActive ? (
            <Tag variant='surface' color='primary' size='sm' label={tSuggestions('cobli_conecta.tag_label')} icon={<Icon name='lightbulb' size='xs' />} />
          ) : (
            <Tag variant='surface' color='attention' size='sm' label={t('aiRecommendations.recommendationTag')} icon={<Icon name='triangle-alert' size='xs' />} />
          )}
          <div className='flex items-center gap-1'>
            {!isSuggestionActive && <AiImage aria-hidden className='size-[19px] text-[#A1A1AA]' />}
            <button
              type='button'
              onClick={() => dismissFloatingCard()}
              aria-label={t('aiRecommendations.close')}
              className='flex size-6 items-center justify-center rounded-lg text-[#737373] transition-colors hover:bg-neutral-100'
            >
              <Icon name='x' size='sm' />
            </button>
          </div>
        </div>

        {/* Carousel of slides */}
        <Carousel data-testid='carousel' setApi={setApi} opts={{ align: 'start' }} className='[&>div]:!rounded-none flex-col'>
          <CarouselContent className='ml-0 transition-[height] duration-200 ease-out' style={{ height: contentHeight }}>
            {slides.map((slide) => (
              <CarouselItem key={slide.key} className='pl-0'>
                {slide.kind === 'suggestion' ? (
                  <CobliConectaSuggestionSlide onAccept={acceptCobliConecta} onDismiss={dismissCobliConecta} />
                ) : (
                  <RecommendationCard
                    recommendation={slide.recommendation}
                    isContextual={isContextual}
                    onDismiss={dismissFloatingCard}
                    dismissLabel={t('aiRecommendations.dismiss')}
                  />
                )}
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        {/* Dot pagination */}
        {slides.length > 1 && (
          <div className='flex w-full items-center justify-center gap-[6px] pt-1'>
            {slides.map((slide, i) => (
              <button
                key={slide.key}
                type='button'
                aria-label={t('aiRecommendations.goToRecommendation', { position: i + 1, total: slides.length })}
                onClick={() => api?.scrollTo(i)}
                className={cn('size-[7px] rounded-full transition-colors', i === selectedIndex ? 'bg-[#0234E1]' : 'bg-[#d9d9e0]')}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AiRecommendationFloatingCard;
